<template>
    <div id="web-chat">
        <div class="route-page-content">
            <WebChatMenu v-model="selectedTab" />
            <div class="page-content-wrapper" v-if="settingInitialized">
                <BubbleActions :update-settings="updateSingleSettings" v-if="isSelected('bubble-actions')" />
                <BubbleSettings :update-settings="updateSingleSettings" v-else-if="isSelected('bubble-settings')" />
                <ChatThemeSettings :update-settings="updateSingleSettings" v-else-if="isSelected('theme-settings')" />
                <TalkSettings :update-settings="updateSingleSettings" v-else-if="isSelected('talk-settings')" />
                <VisitorLoginForm :update-settings="updateSingleSettings"
                                  v-else-if="isSelected('visitor-login-form')" />
                <Catalog v-else-if="isSelected('catalog')" />
                <Triggers v-else-if="isSelected('triggers')" />
                <Details
                  :update-settings="updateSingleSettings"
                  v-else-if="isSelected('details')"
                />
                <WebSiteIntegration v-else-if="isSelected('website-integration')" />
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import WebChatMenu from "./components/WebChatMenu.vue";
import WebChatMenuTypes from "./components/WebChatMenuTypes";
import * as _ from "lodash";
import BubbleActions from "./pages/BubbleActions.vue";
import BubbleSettings from "./pages/BubbleSettings.vue";
import Catalog from "./pages/Catalog.vue";
import ChatThemeSettings from "./pages/ChatThemeSettings.vue";
import TalkSettings from "./pages/TalkSettings.vue";
import Triggers from "./pages/Triggers.vue";
import VisitorLoginForm from "./pages/VisitorLoginForm.vue";
import { PERMISSION_TYPES } from "../../../../language/permissionTypes";
import { sendSocketMessage } from "../../../../utils/socketStream";
import log2 from "../../../../utils/log2";
import Details from "./pages/Details.vue";
import WebSiteIntegration from "../../Settings/pages/WebSiteIntegration.vue";

const log = log2("WebChat");
export default {
    name: "WebChat",
    components: {
        VisitorLoginForm,
        Triggers,
        TalkSettings,
        ChatThemeSettings,
        BubbleActions,
        WebChatMenu,
        BubbleSettings,
        Catalog,
        Details,
        WebSiteIntegration,
    },
    data() {
        return {
            mountedTime: Date.now(),
            submitClicked: false,
            isBusy: false,
            defaultPath: this.$route.path,
            selectedTab: this.$route.path.replace(/(\/integrations\/webchat\/|\/integrations\/webchat)/g, "") || "details",
            handleSave: _.debounce((newSettings) => {
                console.log("handleSave", newSettings);
                const isValid = Date.now() - (this.mountedTime || Date.now()) > 2000; // defaulttan gerçeğe geçerken bir kere sunucuya gitmesini engellemek için alternatif başka çözüm var sa yapalım
                if (isValid) {
                    this.submitClicked = true;
                    const self = this;
                    this.$validator.validateAll().then((response) => {
                        console.log("validateAll", response);
                        if (response) {
                            this.publishSettings(newSettings);
                        } else {
                            self.$message.error({
                                content: self.$t("message.settingFormWrong"),
                                duration: 2,
                                key: "saved",
                            });
                        }
                    });
                }
            }, 750).bind(this),
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            subjects: (state) => state.subject.subjects.data,
            isAdmin: (state) => state.auth.adminUser != null,
            upgrade: (state) => state.app.upgrade,
            settingInitialized: (state) => state.setting.initialized,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
            checkPermission: "auth/checkPermission",
        }),
    },
    methods: {
        ...mapActions({
            getSettings: "setting/getSettings",
            updateSettings: "setting/updateSettings",
            setSettingValue: "setting/setSettingValue",
        }),
        publishSettings(settings, updatingFields) {
            this.updateSettings(settings)
                .then(() => {
                    sendSocketMessage("user settings-updated", {
                        siteId: this.site._id,
                    });
                    this.$message.success({
                        content: this.$t("message.settingsSaved"),
                        duration: 2,
                        key: "saved",
                    });
                })
                .catch((error) => {

                    this.$message.error({
                        content: this.$t("message.settingsCannotSaved"),
                        duration: 2,
                        key: "saveFailed",
                    });
                })
                .finally(() => {
                    this.submitClicked = false;
                });
        },
        updateSingleSettings(key, value, immediately = false) {
            console.log("updateSingleSettings", key, value, immediately);
            if (this.checkPermission(PERMISSION_TYPES["setting.update"].tr.name)) {
                this.setValue(key, value, immediately);
            } else {
                this.$message.error({
                    content: this.$t("message.noAuthorization"),
                    duration: 2,
                    key: "saved",
                });
            }
        },
        async setValue(key, value, immediately) {
            console.log("updateSingleSettings setValue", key, value, immediately);
            if (!this.settingInitialized || this.isBusy) {
                log.warn("setValue", "setting not initialized", { key, value });
                return;
            }
            const payload = {
                setting: key,
                value,
            };
            const newSettings = await this.setSettingValue(payload);
            if (immediately) {
                this.publishSettings(newSettings, payload);
            } else {
                this.handleSave(newSettings, payload);
            }
        },
        isSelected(type) {
            const items = WebChatMenuTypes(this);
            const item = items.find((item) => item?.parentKey === this.selectedTab || item.key === this.selectedTab);
            return item.key === type || item.parentKey === type;
        },
        setDefaultPath: _.debounce(function() {
            this.defaultPath = this.$route.path;
        }, 0),
        init() {
            this.selectedTab = this.defaultPath.replace(/(\/integrations\/webchat\/|\/integrations\/webchat)/g, "") || "details";
            const selected = WebChatMenuTypes(this).find((x) => x.key === this.selectedTab);
            if (!selected?.enabled) {
                const available = WebChatMenuTypes(this).find((x) => x.enabled);
                if (available) {
                    this.selectedTab = available.key;
                } else {
                    this.$router.push("/404");
                }
            }
        },
    },
    created() {
        this.init();
    },
    mounted() {
        this.getSettings().then(() => {
            this.isBusy = false;
        });
        this.mountedTime = Date.now();
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) {
            this.$validator.localize(this.$i18n.locale);
            this.$forceUpdate();
        }
    },
    watch: {
        selectedTab(newVal) {
            this.$router.push(`/integrations/webchat/${newVal}`);
        },
        "$route.path": {
            deep: true,
            handler(val) {
                this.setDefaultPath(val);
            },
        },
        defaultPath(val) {
            this.init(val);
        },
    },
};
</script>
<style lang="scss">
#web-chat {
    display: flex;
    flex-direction: column;

    .page-content-wrapper {
        padding: 10px;
    }

    .bubble-actions,
    .bubble-settings,
    .chat-theme-settings,
    .features,
    .triggers {
        display: flex;
        flex-direction: column;
        gap: 10px;


        .page-header {
            display: flex;
            flex-direction: column;
            gap: 10px;
            border-bottom: 1px solid var(--color-border-default);
            padding-bottom: 10px;

            .title {
                font-size: 18px;
                font-weight: 700;
                color: var(--color-text-strong-default);
            }

            .subtitle {
                font-size: 14px;
                font-weight: 400;
                color: var(--color-text-default);
            }
        }

        section {
            display: flex;
            flex-direction: column;
            gap: 10px;

            .fields {
                display: flex;
                flex-direction: column;
                gap: 20px;

                .field {
                    display: flex;
                    align-items: center;
                    gap: 30px;

                    label {
                        font-size: 15px;
                        font-weight: 600;
                        color: var(--color-text-strong-default);
                        width: 150px;
                        max-width: 150px;
                        display: flex;
                    }

                    .input-wrapper {
                        min-width: 300px;

                        .ant-form-item {
                            margin-bottom: 0;
                        }
                    }
                }
            }
        }
    }
}
</style>
